/** External dependecies */
import React, { useContext, useEffect, useLayoutEffect, useState } from "react";
import NewFooter from "~/assets/js/components/NewFooter";

/** Internal dependecies */
import {
    CheckoutProvider,
    CheckoutContext,
    PAGES,
    ACTIONS,
} from "./CheckoutProvider";
import { CheckoutHeader, CheckoutLayout, CheckoutFooter } from "./components";
import {
    Cart,
    Login,
    Register,
    ContactDetails,
    TimeSlotSelect,
    OrderOverview,
    PaymentSelect,
} from "./sections";
import { DeliveryProvider } from "~/assets/js/models/DeliveryContext";
import { useCartContext } from "~/assets/js/global/cartContext";
import { handleCompletedCartError } from "~/assets/js/global/cartErrorsHandler";
import FullPageLoader from "~/assets/js/components/Loaders/FullPageLoader";
import Banner from "~/assets/js/components/Banner";
import GroupOrderBanner from "~/assets/js/components/GroupOrderBanner";
import useDeliveryTypeChangeListener from "~/assets/js/Helpers/hooks/useDeliveryTypeChangeListener";
import useGroupOrder from "~/assets/js/global/useGroupOrder";
import MemberThankYou from "./sections/MemberThankYou/MemberThankYou";
import Header from "~/assets/js/components/Header";

const Checkout = () => {
    const { cart } = useCartContext();
    const { state: checkoutState, dispatch } = useContext(CheckoutContext);
    const [redirecting, setIsRedirecting] = useState(false);
    const showNewFooter = [PAGES.CART].includes(checkoutState.page);
    const { isOwner, groupCartData, isGroupOrder } = useGroupOrder();

    useLayoutEffect(() => {
        const hasCartErrors = Boolean(
            cart && cart.errors && cart.errors.length > 0
        );
        if (hasCartErrors) {
            const isRedirecting = handleCompletedCartError(cart.errors);
            setIsRedirecting(isRedirecting);
        }
    }, [cart?.errors]);

    useEffect(() => {
        window.posthog?.startSessionRecording?.();
        return () => {
            window.posthog?.stopSessionRecording?.();
        };
    }, []);

    useDeliveryTypeChangeListener({
        onLocationChangeCb: () =>
            dispatch({ type: ACTIONS.RESET_AFTER_LOCATION_CHANGE }),
    });

    const renderPage = (page) => {
        switch (page) {
            case PAGES.CART:
                return <Cart />;
            case PAGES.LOGIN:
                return <Login />;
            case PAGES.CONTACT_DETAILS:
                return <ContactDetails />;
            case PAGES.REGISTER:
                return <Register />;
            case PAGES.PAYMENT_SELECT:
                return <PaymentSelect />;
            case PAGES.TIME_SLOT_SELECT:
                return <TimeSlotSelect />;
            case PAGES.ORDER_OVERVIEW:
                return <OrderOverview />;
            case PAGES.MEMBER_THANK_YOU:
                return <MemberThankYou />;
            default:
                return <div>404 Page not found :/</div>;
        }
    };

    if (redirecting) {
        return <FullPageLoader />;
    }

    return (
        <>
            {checkoutState.page === PAGES.MEMBER_THANK_YOU ? (
                <Header />
            ) : (
                <CheckoutHeader />
            )}

            {checkoutState.page !== PAGES.MEMBER_THANK_YOU && (
                <>
                    <Banner forceHide={isGroupOrder} />
                    {isGroupOrder && (
                        <GroupOrderBanner
                            isOwner={isOwner}
                            ownerName={groupCartData?.owner_name || ""}
                            slotEnd={groupCartData?.slot_valid_until || ""}
                        />
                    )}
                </>
            )}

            <CheckoutLayout>{renderPage(checkoutState.page)}</CheckoutLayout>

            {showNewFooter ? <NewFooter /> : <CheckoutFooter />}
        </>
    );
};

const CheckoutApp = () => {
    return (
        <DeliveryProvider>
            <CheckoutProvider>
                <Checkout />
            </CheckoutProvider>
        </DeliveryProvider>
    );
};

export default CheckoutApp;
